import { COLLECTION_TYPES } from '@/core/collections/constants';
import * as jsonpatch from 'fast-json-patch';
import { registerTemplate, unregisterTemplate } from '@mythictable/sheets';
import type { SheetDefinition } from '@mythictable/sheets';

// TODO: type this — Vuex module context
/* eslint-disable @typescript-eslint/no-explicit-any */
type Ctx = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

type TemplateRecord = Record<string, unknown> & { _id: string };

export const getters = {
    getTemplates: (_state: Ctx, _getters: Ctx, _rootState: Ctx, rootGetters: Ctx) => () => {
        return rootGetters['collections/getCollection'](COLLECTION_TYPES.templates) as Record<string, TemplateRecord>;
    },
    getTemplate: (_state: Ctx, _getters: Ctx, _rootState: Ctx, rootGetters: Ctx) => (id: string) => {
        return rootGetters['collections/getItem'](COLLECTION_TYPES.templates, id) as TemplateRecord | null;
    },
};

export const actions = {
    async load({ dispatch }: Ctx): Promise<unknown> {
        const results = await dispatch('collections/load', { collection: COLLECTION_TYPES.templates }, { root: true });
        dispatch('register');
        return results;
    },

    register({ getters }: Ctx): void {
        const templates = getters.getTemplates() as Record<string, TemplateRecord>;
        for (const definition of Object.values(templates)) {
            registerTemplate(definition as unknown as SheetDefinition);
        }
    },

    unregister(_ctx: Ctx, ids: string[]): void {
        for (const id of ids) {
            unregisterTemplate(id);
        }
    },

    async add({ dispatch, rootState }: Ctx, definition: Record<string, unknown>): Promise<unknown> {
        const item = {
            ...definition,
            _userid: rootState.profile.me.id,
            _campaign: rootState.live.sessionId,
        };
        const created = await dispatch(
            'collections/add',
            { collection: COLLECTION_TYPES.templates, item },
            { root: true },
        );
        dispatch('register');
        return created;
    },

    async update({ dispatch, getters }: Ctx, editedTemplate: TemplateRecord): Promise<unknown> {
        const current = getters.getTemplate(editedTemplate._id);
        if (!current) throw new Error(`Could not find template to update with id '${editedTemplate._id}'`);

        const patch = jsonpatch.compare(current, editedTemplate).filter((op) => !op.path.startsWith('/_'));
        if (patch.length === 0) return null;

        const result = await dispatch(
            'collections/update',
            { collection: COLLECTION_TYPES.templates, id: editedTemplate._id, patch },
            { root: true },
        );
        dispatch('register');
        return result;
    },

    async remove({ dispatch }: Ctx, { id }: { id: string }): Promise<unknown> {
        return await dispatch('collections/remove', { collection: COLLECTION_TYPES.templates, id }, { root: true });
    },
};

const TemplateStore = {
    namespaced: true,
    getters,
    actions,
};

export default TemplateStore;
